import { useState, useEffect, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Input from "@/v2-ui/input"
import { useSearchParams } from "@/v2-backoffice-client/utils"
import { useTableContext } from "./table.context"

export type TableSearchProps = Omit<ComponentProps<"input">, "size"> & {
  /** Debounce delay in ms. Defaults to 300. */
  debounce?: number
  /**
   * Column to filter on (server-side). Sets a column filter with "contains"
   * operator. When not provided, falls back to table globalFilter (client-side).
   */
  column?: string
  /**
   * Query-string key the search text is read from on mount and written to
   * as it changes (replace, never a history entry; removed when empty), so
   * a board can be deep-linked with a search already applied. Off by
   * default: most searches are a moment's filter, not a place.
   */
  urlParam?: string
}

function TableSearch(props: TableSearchProps) {
  const {
    placeholder = "Search...",
    className,
    debounce = 300,
    column,
    urlParam,
    ...inputProps
  } = props

  const { table } = useTableContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [value, setValue] = useState(
    () => (urlParam ? searchParams.get(urlParam) : null) ?? "",
  )

  useEffect(() => {
    const t = setTimeout(() => {
      if (column) {
        table.getColumn(column)?.setFilterValue(value || undefined)
      } else {
        table.setGlobalFilter(value || undefined)
      }
      if (urlParam && (searchParams.get(urlParam) ?? "") !== value) {
        // The wrapper merges with the other params and drops the key on "".
        setSearchParams({ [urlParam]: value }, { replace: true })
      }
    }, debounce)
    return () => {
      clearTimeout(t)
    }
  }, [value, debounce, table, column, urlParam, searchParams, setSearchParams])

  return (
    <div className={cn("ui-table-search-wrapper", className)}>
      <Icon src="search" className="ui-table-search-icon" size={12} />
      <Input
        className="ui-table-search"
        type="search"
        size="xs"
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          setValue(e.target.value)
        }}
        {...inputProps}
      />
    </div>
  )
}

export default TableSearch
